import React, { useState, useRef, useEffect } from "react";

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  position?: "top" | "bottom" | "left" | "right";
  className?: string;
}

const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = "bottom",
  className = "",
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const calculatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = triggerRect.bottom + 8;
    let left = triggerRect.left + triggerRect.width / 2;

    // Use actual tooltip width for accurate positioning
    const tooltipWidth = tooltipRect.width || 200;
    const leftBoundary = 16;
    const rightBoundary = viewportWidth - 16;

    // Ensure tooltip doesn't go off-screen horizontally
    if (left - tooltipWidth / 2 < leftBoundary) {
      left = leftBoundary + tooltipWidth / 2;
    } else if (left + tooltipWidth / 2 > rightBoundary) {
      left = rightBoundary - tooltipWidth / 2;
    }

    // If tooltip would go off-screen vertically, position it above
    const tooltipHeight = tooltipRect.height || 40;
    if (top + tooltipHeight > viewportHeight) {
      top = triggerRect.top - tooltipHeight - 8;
    }

    setTooltipStyle({
      position: "fixed",
      top: `${top}px`,
      left: `${left}px`,
      transform: "translateX(-50%)",
      maxWidth: "calc(100vw - 32px)",
      whiteSpace: "nowrap",
      zIndex: 9999,
    });
  };

  useEffect(() => {
    if (isVisible) {
      // Initial positioning
      calculatePosition();

      // Recalculate after a brief delay to ensure tooltip is rendered
      const timer = setTimeout(() => {
        calculatePosition();
      }, 10);

      return () => clearTimeout(timer);
    }
  }, [isVisible, content]);

  return (
    <div className={`relative inline-block ${className}`}>
      <div
        ref={triggerRef}
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onFocus={() => setIsVisible(true)}
        onBlur={() => setIsVisible(false)}
      >
        {children}
      </div>

      {isVisible && (
        <div
          ref={tooltipRef}
          className="px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm text-white bg-gray-800 dark:bg-gray-200 dark:text-gray-800 rounded-lg shadow-lg pointer-events-none"
          role="tooltip"
          style={tooltipStyle}
        >
          {content}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
